import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';

import { IconComponent } from '../icon/icon.component';

export interface IHorizontalFilterItem {
  id: string;
  label: string;
  value: string;
  isSelected?: boolean;
  isDisabled?: boolean;
}

export interface IHorizontalFilterConfiguration {
  items: IHorizontalFilterItem[];
}

export interface IHorizontalFilterEvent {
  item: IHorizontalFilterItem;
  index: number;
}

@Component({
  standalone: true,
  imports: [CommonModule, IconComponent],
  selector: 'nrc-horizontal-filter',
  templateUrl: './horizontal-filter.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HorizontalFilterComponent implements AfterViewInit {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  @Input({ required: true }) public config!: IHorizontalFilterConfiguration;
  @Output() public selectionChange = new EventEmitter<IHorizontalFilterEvent>();

  @ViewChild('scrollContainer') private scrollContainer!: ElementRef<HTMLDivElement>;

  public showRightArrow = false;

  /**
   *
   */
  public ngAfterViewInit(): void {
    this.checkOverflow();

    // Listen for window resize to update arrow visibility
    window.addEventListener('resize', () => this.checkOverflow());
  }

  /**
   * Check if content overflows and update arrow visibility.
   */
  private checkOverflow(): void {
    if (this.scrollContainer) {
      const element = this.scrollContainer.nativeElement;
      const hasOverflow = element.scrollWidth > element.clientWidth;

      // If the arrow visibility is the same as the overflow, do nothing.
      if (this.showRightArrow === hasOverflow) {
        return;
      }

      // Update the arrow visibility, need to trigger change detection to update the template.
      this.showRightArrow = hasOverflow;
      this.changeDetectorRef.detectChanges();
    }
  }

  /**
   * Handle item click event.
   *
   * @param item
   */
  public handleItemClick(item: IHorizontalFilterItem): void {
    if (item.isDisabled) {
      return;
    }

    const index = this.config.items.findIndex(i => i.id === item.id);

    this.selectionChange.emit({ item, index });
  }

  /**
   * Scroll left in the container.
   */
  public scrollLeft(): void {
    if (!this.scrollContainer) {
      return;
    }

    this.scrollContainer.nativeElement.scrollBy({
      left: -200,
      behavior: 'smooth'
    });
  }

  /**
   * Scroll right in the container.
   */
  public scrollRight(): void {
    if (!this.scrollContainer) {
      return;
    }

    this.scrollContainer.nativeElement.scrollBy({
      left: 200,
      behavior: 'smooth'
    });
  }
}
